import { useCoinMetricsApiKeyInvalid } from '@src/providers/CoinMetricsApiProvider';
import { HStack, LoaderTalos, Text, VStack, type BoxProps } from '@talos/kyoko';
import type { ReactNode } from 'react';
import styled from 'styled-components';

const LOADER_WRAPPER_STYLE = { width: '100%', height: '100%' };

/**
 * Shown for any Intel module error once the org's CoinMetrics API key has been rejected. Every
 * module is powered by the same `ui.CoinMetricsApiKey`, so a bad key fails all of them identically
 * — this message takes precedence over each module's generic copy so the real cause is clear.
 */
export const INTEL_INVALID_API_KEY_MESSAGE = 'Invalid CoinMetrics API key. Contact your administrator to update it.';

// Omit `title` so a caller can't pass it expecting header content — it would silently
// become a DOM `title` attribute via `...boxProps`. Header content goes through `header`.
interface SignalsModuleShellProps extends Omit<BoxProps, 'title'> {
  /** Left slot of the module header — typically a toolbar or selector component. */
  header?: ReactNode;
  /** Right-aligned controls in the header (e.g. asset dropdown + time range toggle). */
  controls?: ReactNode;
  isLoading?: boolean;
  /** Custom loading UI shown (instead of the default centered spinner) while `isLoading` is true. */
  loadingContent?: ReactNode;
  isError?: boolean;
  /** Shown when isError (or isPermissionError) is true. Defaults to a generic message. */
  errorMessage?: ReactNode;
  /**
   * True for an entitlement gap (the key is valid but lacks access to a required metric), as opposed
   * to a recoverable `isError` cause like truncated data. Implies `isError`, and additionally hides
   * the header — every header control is a dead end when the cause is permissions, unlike e.g.
   * truncated data, where the header's own controls (time range, frequency) are how the user fixes it.
   */
  isPermissionError?: boolean;
  children: ReactNode;
}

/** Layout shell shared by every Signals module: a header (left slot + right controls) and a body for chart or loader. */
export function SignalsModuleShell({
  header,
  controls,
  isLoading,
  loadingContent,
  isError,
  errorMessage = 'Failed to load data.',
  isPermissionError,
  children,
  ...boxProps
}: SignalsModuleShellProps) {
  // A rejected org API key fails every Intel module the same way, so surface that specific cause
  // instead of the module's generic message whenever the key is the reason the request failed.
  const apiKeyInvalid = useCoinMetricsApiKeyInvalid();
  const resolvedErrorMessage = apiKeyInvalid ? INTEL_INVALID_API_KEY_MESSAGE : errorMessage;
  // A rejected key is terminal for the whole module, independent of the module's own `isError`
  // (which may only track its primary query). Surface it even when a secondary request — a catalog
  // pre-warm or asset-map fetch — is what tripped the global flag, so the panel never renders a
  // headerless empty body with no explanation.
  const showError = isError || apiKeyInvalid || isPermissionError;
  const hideHeader = apiKeyInvalid || isPermissionError;
  return (
    <VStack flex="1" w="100%" h="100%" {...boxProps}>
      {!hideHeader && (
        <Header h="40px" w="100%" px="spacing12" justifyContent="space-between" data-testid="intel-module-header">
          {header}
          {controls}
        </Header>
      )}
      <VStack flex="1" w="100%" overflow="hidden">
        {showError ? (
          <HStack h="100%" w="100%" justifyContent="center" alignItems="center">
            <Text color="colorTextNegative">{resolvedErrorMessage}</Text>
          </HStack>
        ) : isLoading ? (
          (loadingContent ?? <LoaderTalos wrapperStyle={LOADER_WRAPPER_STYLE} delay={300} />)
        ) : (
          children
        )}
      </VStack>
    </VStack>
  );
}

const Header = styled(HStack)`
  border-bottom: solid 1px ${({ theme }) => theme.colors.gray['010']};
  flex-shrink: 0;
`;
